import React, { useEffect } from 'react';
import { Plus } from 'lucide-react';
import InviteCodesList from './InviteCodesList';
import useInviteCodes from '../../hooks/useInviteCodes';

/**
 * InviteCodesSection Component
 * Main container for the invite codes management tab
 * Handles fetching, displaying, and managing invite codes
 *
 * @param {Object} props - Component props
 * @param {Function} props.onGenerateClick - Callback when "Generate Code" button is clicked
 */
const InviteCodesSection = ({ onGenerateClick }) => {
  const {
    inviteCodes,
    loading,
    fetchInviteCodes,
    copyToClipboard
  } = useInviteCodes();

  // Fetch invite codes when component mounts
  useEffect(() => {
    fetchInviteCodes();
  }, []);

  const handleDeleteCode = async (codeId) => {
    // TODO: Implement delete functionality when API endpoint is available
    console.log('Delete code:', codeId);
    // For now, just show a message
    // Once implemented, this should call an API endpoint and refresh the list
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Invite Code Management</h2>
          <p className="text-sm text-gray-500 mt-1">
            Generate and manage invite codes for new users
          </p>
        </div>
        <button
          onClick={onGenerateClick}
          disabled={loading}
          className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          Generate Code
        </button>
      </div>

      {/* Statistics Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <p className="text-sm text-blue-600 font-medium">Total Codes</p>
          <p className="text-2xl font-bold text-blue-900 mt-1">{inviteCodes.length}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <p className="text-sm text-green-600 font-medium">Active Codes</p>
          <p className="text-2xl font-bold text-green-900 mt-1">
            {inviteCodes.filter(code =>
              code.status === 'ACTIVE' &&
              new Date(code.expiresAt) > new Date() &&
              code.currentUses < code.maxUses
            ).length}
          </p>
        </div>
        <div className="bg-orange-50 rounded-lg p-4">
          <p className="text-sm text-orange-600 font-medium">Total Uses</p>
          <p className="text-2xl font-bold text-orange-900 mt-1">
            {inviteCodes.reduce((sum, code) => sum + (code.currentUses || 0), 0)}
          </p>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <p className="text-sm text-purple-600 font-medium">Expired</p>
          <p className="text-2xl font-bold text-purple-900 mt-1">
            {inviteCodes.filter(code =>
              code.status === 'EXPIRED' || new Date(code.expiresAt) < new Date()
            ).length}
          </p>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
        </div>
      )}

      {/* Invite Codes List */}
      {!loading && (
        <InviteCodesList
          inviteCodes={inviteCodes}
          onCopyCode={copyToClipboard}
          onDeleteCode={handleDeleteCode}
        />
      )}
    </div>
  );
};

export default InviteCodesSection;
